import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import * as api from "../api/client";
import { pixelTheme } from "../theme/pixel";
import { primaryBtn, withBtnStyle } from "../theme/styles";
import { inertAttrs } from "../logic/inert";

export function ForgotPasswordPage() {
  const theme = pixelTheme;
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    const value = email.trim();
    if (!value) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.forgotPassword(value);
      // The server returns 200 whether or not the email is in our
      // DB, so we show the same confirmation either way. Otherwise
      // a user could probe which addresses we have on file.
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100%",
        background: theme.pageBg,
        color: theme.ink,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: theme.fontBody,
        padding: 20,
      }}
    >
      <form
        onSubmit={onSubmit}
        style={{
          background: theme.panel,
          border: `2px solid ${theme.panelEdgeStrong}`,
          padding: 28,
          width: "100%",
          maxWidth: 460,
          display: "grid",
          gap: 18,
        }}
      >
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 16,
            letterSpacing: theme.letterSpacing,
            color: theme.accent,
            textAlign: "center",
          }}
        >
          RESET YOUR PASSWORD
        </div>

        {done ? (
          <>
            <div style={{ fontSize: 14, color: theme.inkDim, lineHeight: 1.5 }}>
              If that email is registered, we've sent a reset link.
              Check your inbox (and spam folder) in the next minute or
              two. The link is good for 15 minutes.
            </div>
            <div style={{ textAlign: "center", fontSize: 12 }}>
              <Link to="/login" style={{ color: theme.inkDim }}>
                Back to login
              </Link>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 14, color: theme.inkDim }}>
              Enter your email and we'll send a reset link.
            </div>
            <label
              style={{
                display: "grid",
                gap: 6,
                fontFamily: theme.fontDisplay,
                fontSize: 11,
                letterSpacing: theme.letterSpacing,
                color: theme.inkDim,
              }}
            >
              EMAIL
              <input
                autoFocus
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
                className="doi-input"
                style={{
                  fontFamily: theme.fontBody,
                  fontSize: 20,
                  padding: "8px 10px",
                  background: theme.bgDeep,
                  color: theme.ink,
                  border: `2px solid ${theme.panelEdge}`,
                }}
              />
            </label>
            {error && (
              <div style={{ color: theme.accent, fontSize: 14 }}>{error}</div>
            )}
            <button
              type="submit"
              {...inertAttrs(submitting || email.trim() === "")}
              {...withBtnStyle(primaryBtn(theme, submitting), {
                fontSize: 12,
                padding: "10px 16px",
              })}
            >
              {submitting ? "…" : "SEND RESET LINK"}
            </button>
            <div style={{ textAlign: "center", fontSize: 12 }}>
              <Link to="/login" style={{ color: theme.inkDim }}>
                Back to login
              </Link>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
